<template>
  <div class="reward-program-statement">
    <div class="statement-summary-grid">
      <article class="reward-program-statement-card statement-summary-card">
        <p class="statement-summary-label">
          {{ $t("rewardProgram.statementLatestBalance") }}
        </p>
        <p class="statement-summary-value positive">
          {{ formatPoints(latestPointsBalance) }}
        </p>
      </article>

      <article class="reward-program-statement-card statement-summary-card">
        <p class="statement-summary-label">
          {{ $t("rewardProgram.statementMovementCount") }}
        </p>
        <p class="statement-summary-value neutral">
          {{ sortedHistory.length }}
        </p>
      </article>

      <article class="reward-program-statement-card statement-summary-card">
        <p class="statement-summary-label">
          {{ $t("rewardProgram.statementCurrentLevelLabel") }}
        </p>
        <div class="statement-current-level">
          <img
            v-if="currentLevelBadge"
            :src="currentLevelBadge"
            :alt="formattedCurrentLevel"
            class="statement-current-level-badge"
          />
          <p class="statement-summary-value neutral">
            {{ formattedCurrentLevel || "-" }}
          </p>
        </div>
      </article>
    </div>

    <p v-if="isUsingMockHistory" class="statement-helper">
      {{ $t("rewardProgram.statementMockHelper") }}
    </p>

    <section v-if="sortedHistory.length" class="statement-timeline">
      <article
        v-for="event in sortedHistory"
        :key="`${event.eventType}-${event.event}-${event.date}`"
        class="statement-event"
      >
        <div class="statement-event-rail">
          <div class="statement-event-line"></div>
          <div
            class="statement-event-icon"
            :class="`is-${getEventTheme(event).theme}`"
          >
            <img
              v-if="getEventBadge(event)"
              :src="getEventBadge(event)"
              :alt="getEventTitle(event)"
              class="statement-event-badge"
            />
            <b-icon v-else :icon="getEventTheme(event).icon"></b-icon>
          </div>
        </div>

        <section
          class="reward-program-statement-card statement-event-card"
          :class="`is-${getEventTheme(event).theme}`"
        >
          <div class="statement-event-header">
            <div>
              <p class="statement-event-date">{{ formatDate(event.date) }}</p>
              <h3 class="title is-5">{{ getEventTitle(event) }}</h3>
              <p class="statement-event-description">{{ event.description }}</p>
            </div>

            <div class="statement-event-highlight">
              <span class="statement-event-period">
                {{ formatEventBadgeLabel(event) }}
              </span>
              <strong
                v-if="hasPointsMovement(event)"
                class="statement-event-points"
                :class="{
                  positive: !isDebitEvent(event),
                  negative: isDebitEvent(event)
                }"
              >
                {{ formatSignedPoints(event) }}
              </strong>
            </div>
          </div>

          <div
            class="statement-event-details"
            :class="{
              'is-compact-balance-event': ['bonus', 'redemption'].includes(
                event.eventType
              )
            }"
          >
            <article
              v-if="event.eventType === 'quarterlyLevel'"
              class="statement-detail-card statement-detail-card-level-flow"
            >
              <div
                class="statement-level-transition statement-level-transition-plain"
              >
                <div
                  v-if="showQuarterlyPreviousLevel(event)"
                  class="statement-level-pill statement-level-pill-previous"
                >
                  {{ formatLevel(event.currentLevel) }}
                </div>

                <div
                  v-if="showQuarterlyPreviousLevel(event)"
                  class="statement-level-arrow"
                  :class="`is-${getQuarterlyLevelChange(event).theme}`"
                >
                  <b-icon :icon="getQuarterlyLevelChange(event).icon"></b-icon>
                </div>

                <div class="statement-level-pill statement-level-pill-next">
                  {{ formatLevel(event.newLevel) }}
                </div>
              </div>
            </article>

            <article
              v-if="event.eventType === 'levelAchievmentBonus'"
              class="statement-detail-card statement-detail-card-achievement"
            >
              <div class="statement-achievement-banner">
                <img
                  v-if="getEventBadge(event)"
                  :src="getEventBadge(event)"
                  :alt="formatLevel(event.currentLevel)"
                  class="statement-achievement-badge"
                />
                <span>
                  {{ formatLevel(event.currentLevel) }}
                </span>
              </div>
            </article>

            <article
              v-for="detail in getEventDetails(event)"
              :key="`${event.eventType}-${event.event}-${detail.label}`"
              class="statement-detail-card"
            >
              <span class="statement-detail-label">{{ detail.label }}</span>
              <strong class="statement-detail-value">{{ detail.value }}</strong>
            </article>
          </div>
        </section>
      </article>
    </section>

    <section v-else class="reward-program-statement-card statement-empty-state">
      <p class="statement-empty-title">
        {{ $t("rewardProgram.statementEmptyTitle") }}
      </p>
    </section>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import badgeStart from "../../assets/img/badges/badge-start.png";
import badgeSilver from "../../assets/img/badges/badge-silver.png";
import badgeGold from "../../assets/img/badges/badge-gold.png";
import badgePlatinum from "../../assets/img/badges/badge-platinum.png";
import badgeDiamond from "../../assets/img/badges/badge-diamond.png";
import badgeBlack from "../../assets/img/badges/badge-black.png";
import badgeInfinity from "../../assets/img/badges/badge-infinity.png";

export default {
  props: {
    history: {
      type: Array,
      default: () => []
    },
    isUsingMockHistory: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    ...mapGetters(["locale", "rewardProgram"]),

    sortedHistory() {
      return [...this.history].sort((firstEvent, secondEvent) => {
        return (
          new Date(secondEvent.date).getTime() -
          new Date(firstEvent.date).getTime()
        );
      });
    },

    latestPointsBalance() {
      const latestPointsEvent = this.sortedHistory.find(
        event => typeof event.pointsAfter === "number"
      );

      if (latestPointsEvent) {
        return latestPointsEvent.pointsAfter;
      }

      return this.rewardProgram.points || 0;
    },

    formattedCurrentLevel() {
      return this.formatLevel(this.rewardProgram.currentLevel);
    },

    currentLevelBadge() {
      return this.getBadgeImage(this.rewardProgram.currentLevel);
    },

    normalizedLocale() {
      return (this.locale || "pt_BR").replace("_", "-");
    }
  },

  methods: {
    getBadgeImage(level) {
      const normalizedLevel = (level || "").toLowerCase();

      const badges = {
        start: badgeStart,
        silver: badgeSilver,
        gold: badgeGold,
        platinum: badgePlatinum,
        diamond: badgeDiamond,
        black: badgeBlack,
        infinity: badgeInfinity
      };

      return badges[normalizedLevel] || null;
    },

    getEventBadge(event) {
      if (event.eventType === "quarterlyLevel") {
        return this.getBadgeImage(event.newLevel);
      }

      if (event.eventType === "levelAchievmentBonus") {
        return this.getBadgeImage(event.currentLevel);
      }

      return null;
    },

    getEventTheme(event) {
      const themes = {
        quarterlyLevel: {
          theme: "amber",
          icon: "arrow-up"
        },
        monthlyBonus: {
          theme: "green",
          icon: "star"
        },
        levelAchievmentBonus: {
          theme: "emerald",
          icon: "check-circle"
        },
        bonus: {
          theme: "green",
          icon: "plus-circle"
        },
        redemption: {
          theme: "red",
          icon: "tag"
        }
      };

      return themes[event.eventType] || themes.bonus;
    },

    getLevelRank(level) {
      const orderedLevels = [
        "start",
        "silver",
        "gold",
        "platinum",
        "diamond",
        "black",
        "infinity"
      ];

      return orderedLevels.indexOf((level || "").toLowerCase());
    },

    getQuarterlyLevelChange(event) {
      const previousLevelRank = this.getLevelRank(event.currentLevel);
      const nextLevelRank = this.getLevelRank(event.newLevel);

      if (previousLevelRank === -1 || nextLevelRank === -1) {
        return {
          icon: "arrow-up",
          theme: "up"
        };
      }

      if (nextLevelRank > previousLevelRank) {
        return {
          icon: "arrow-right",
          theme: "up"
        };
      }

      if (nextLevelRank < previousLevelRank) {
        return {
          icon: "arrow-right",
          theme: "down"
        };
      }

      return {
        icon: "equals",
        theme: "same"
      };
    },

    isFirstQuarterlyLevelEvent(event) {
      const firstQuarterlyLevelEvent = [...this.history]
        .filter(historyItem => historyItem.eventType === "quarterlyLevel")
        .sort((firstEvent, secondEvent) => {
          return (
            new Date(firstEvent.date).getTime() -
            new Date(secondEvent.date).getTime()
          );
        })[0];

      if (!firstQuarterlyLevelEvent) {
        return false;
      }

      return (
        firstQuarterlyLevelEvent.eventType === event.eventType &&
        firstQuarterlyLevelEvent.event === event.event &&
        firstQuarterlyLevelEvent.date === event.date
      );
    },

    showQuarterlyPreviousLevel(event) {
      return (
        Boolean(event.currentLevel) && !this.isFirstQuarterlyLevelEvent(event)
      );
    },

    getEventTitle(event) {
      const titles = {
        quarterlyLevel: this.$t("rewardProgram.eventTypes.quarterlyLevel"),
        monthlyBonus: this.$t("rewardProgram.eventTypes.monthlyBonus"),
        levelAchievmentBonus: this.$t(
          "rewardProgram.eventTypes.levelAchievmentBonus"
        ),
        bonus: this.$t("rewardProgram.eventTypes.bonus"),
        redemption: this.$t("rewardProgram.eventTypes.redemption")
      };

      return titles[event.eventType] || event.eventType;
    },

    getEventDetails(event) {
      if (event.eventType === "quarterlyLevel") {
        return [
          {
            label: this.getQuarterMonthLabel(event.event, 0),
            value: this.formatCurrency(event.firstMonthRevenue)
          },
          {
            label: this.getQuarterMonthLabel(event.event, 1),
            value: this.formatCurrency(event.secondMonthRevenue)
          },
          {
            label: this.getQuarterMonthLabel(event.event, 2),
            value: this.formatCurrency(event.thirdMonthRevenue)
          },
          {
            label: this.$t("rewardProgram.statementFields.averageRevenue"),
            value: this.formatCurrency(event.averageRevenue)
          }
        ];
      }

      if (event.eventType === "monthlyBonus") {
        return [
          {
            label: this.$t("rewardProgram.statementFields.currentLevel"),
            value: this.formatLevel(event.currentLevel)
          },
          {
            label: this.$t("rewardProgram.statementFields.monthlyRevenue"),
            value: this.formatCurrency(event.monthlyRevenue)
          },
          {
            label: this.$t("rewardProgram.statementFields.currentMultiplier"),
            value: this.formatMultiplier(event.currentMultiplier)
          },
          {
            label: this.$t("rewardProgram.statementFields.pointsBefore"),
            value: this.formatPoints(event.pointsBefore)
          },
          {
            label: this.$t("rewardProgram.statementFields.pointsAfter"),
            value: this.formatPoints(event.pointsAfter)
          }
        ];
      }

      if (event.eventType === "levelAchievmentBonus") {
        return [
          {
            label: this.$t("rewardProgram.statementFields.currentMultiplier"),
            value: this.formatMultiplier(event.currentMultiplier)
          },
          {
            label: this.$t("rewardProgram.statementFields.pointsBefore"),
            value: this.formatPoints(event.pointsBefore)
          },
          {
            label: this.$t("rewardProgram.statementFields.pointsAfter"),
            value: this.formatPoints(event.pointsAfter)
          }
        ];
      }

      return [
        {
          label: this.$t("rewardProgram.statementFields.pointsBefore"),
          value: this.formatPoints(event.pointsBefore)
        },
        {
          label: this.$t("rewardProgram.statementFields.pointsAfter"),
          value: this.formatPoints(event.pointsAfter)
        }
      ];
    },

    hasPointsMovement(event) {
      return typeof event.points === "number";
    },

    isDebitEvent(event) {
      return event.eventType === "redemption";
    },

    formatSignedPoints(event) {
      const normalizedPoints = Math.abs(event.points || 0);
      const prefix = this.isDebitEvent(event) ? "-" : "+";
      return `${prefix}${this.formatPoints(normalizedPoints)}`;
    },

    formatPoints(value) {
      return `${Number(value || 0).toLocaleString(this.normalizedLocale)} pts`;
    },

    formatCurrency(value) {
      return new Intl.NumberFormat(this.normalizedLocale, {
        style: "currency",
        currency: "BRL"
      }).format(Number(value || 0) / 100);
    },

    formatDate(value) {
      return new Intl.DateTimeFormat(this.normalizedLocale, {
        dateStyle: "long",
        timeStyle: "short"
      }).format(new Date(value));
    },

    formatLevel(level) {
      if (!level) {
        return "";
      }

      return level.charAt(0).toUpperCase() + level.slice(1);
    },

    formatMultiplier(value) {
      return new Intl.NumberFormat(this.normalizedLocale, {
        style: "percent",
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(Number(value || 0));
    },

    formatEventBadgeLabel(event) {
      if (event.eventType === "redemption") {
        return this.$t("rewardProgram.eventBadgeLabels.redemption");
      }

      if (event.eventType === "levelAchievmentBonus") {
        return this.$t("rewardProgram.eventBadgeLabels.level", {
          level: this.formatLevel(event.currentLevel)
        });
      }

      return event.event;
    },

    getQuarterMonthLabel(eventCode, monthOffset) {
      const quarterMatch = String(eventCode || "").match(/^(\d{2})T(\d)$/);

      if (!quarterMatch) {
        return this.$t("rewardProgram.statementFields.averageRevenue");
      }

      const year = 2000 + Number(quarterMatch[1]);
      const quarter = Number(quarterMatch[2]);
      const monthIndex = (quarter - 1) * 3 + monthOffset;
      const date = new Date(year, monthIndex, 1);

      return new Intl.DateTimeFormat(this.normalizedLocale, {
        month: "long"
      }).format(date);
    }
  }
};
</script>

<style scoped>
.reward-program-statement {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.reward-program-statement-card {
  padding: 1.1rem 1.2rem;
  background-color: white;
  border-radius: 6px;
  box-shadow: 0 2px 10px rgba(10, 10, 10, 0.06);
}

.statement-source-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.3rem 0.55rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 800;
  white-space: nowrap;
}

.statement-source-badge.is-mock {
  background: #fff4e8;
  color: #bf6a02;
}

.statement-source-badge.is-api {
  background: #ebfff2;
  color: #12733b;
}

.statement-summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
}

.statement-summary-card {
  border-top: 4px solid #ececec;
}

.statement-summary-label {
  color: #727272;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.statement-summary-value {
  margin-top: 0.6rem;
  font-size: 1.55rem;
  font-weight: 800;
  line-height: 1.1;
}

.statement-summary-value.positive {
  color: #17834a;
}

.statement-summary-value.neutral {
  color: #2f2f2f;
}

.statement-current-level {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.6rem;
}

.statement-current-level-badge {
  width: 48px;
  height: 48px;
  object-fit: contain;
}

.statement-helper {
  margin: -0.25rem 0 0;
  color: #7a5b0d;
  font-size: 0.92rem;
}

.statement-timeline {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.statement-event {
  display: grid;
  grid-template-columns: 58px minmax(0, 1fr);
  gap: 0.85rem;
}

.statement-event-rail {
  position: relative;
  display: flex;
  justify-content: center;
}

.statement-event-line {
  position: absolute;
  top: 0;
  bottom: -1rem;
  width: 2px;
  background: linear-gradient(180deg, #f2f2f2 0%, #dfdfdf 100%);
}

.statement-event:last-child .statement-event-line {
  bottom: 50%;
}

.statement-event-icon {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 16px;
  background: #fafafa;
  border: 1px solid #ececec;
  box-shadow: 0 10px 20px rgba(10, 10, 10, 0.08);
}

.statement-event-icon.is-green,
.statement-event-card.is-green {
  border-color: #daf1e1;
}

.statement-event-icon.is-emerald,
.statement-event-card.is-emerald {
  border-color: #d1f3e4;
}

.statement-event-icon.is-red,
.statement-event-card.is-red {
  border-color: #f6d4d6;
}

.statement-event-icon.is-amber,
.statement-event-card.is-amber {
  border-color: #f3dfb6;
}

.statement-event-icon.is-green {
  color: #17834a;
  background: #effbf3;
}

.statement-event-icon.is-emerald {
  color: #0c8c68;
  background: #ebfff8;
}

.statement-event-icon.is-red {
  color: #c73b47;
  background: #fff2f3;
}

.statement-event-icon.is-amber {
  color: #b27400;
  background: #fff8e9;
}

.statement-event-badge {
  width: 30px;
  height: 30px;
  object-fit: contain;
}

.statement-event-card {
  border-left: 5px solid transparent;
}

.statement-event-card.is-green {
  border-left-color: #17834a;
}

.statement-event-card.is-emerald {
  border-left-color: #0c8c68;
}

.statement-event-card.is-red {
  border-left-color: #c73b47;
}

.statement-event-card.is-amber {
  border-left-color: #b27400;
}

.statement-event-header {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
}

.statement-event-date {
  color: #6c6c6c;
  font-size: 0.8rem;
  margin-bottom: 0.2rem;
}

.statement-event-description {
  color: #585858;
  line-height: 1.4;
  font-size: 0.94rem;
}

.statement-event-highlight {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.45rem;
}

.statement-event-period {
  display: inline-flex;
  align-items: center;
  padding: 0.22rem 0.5rem;
  border-radius: 999px;
  background: #f4f4f4;
  color: #4d4d4d;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.statement-event-points {
  font-size: 1.15rem;
  font-weight: 800;
  white-space: nowrap;
}

.statement-event-points.positive {
  color: #17834a;
}

.statement-event-points.negative {
  color: #c73b47;
}

.statement-level-transition,
.statement-achievement-banner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  width: 100%;
  min-height: 100%;
  padding: 0.7rem 0.85rem;
  border-radius: 6px;
}

.statement-level-transition {
  background: #fafafa;
}

.statement-level-transition.statement-level-transition-plain {
  background: transparent;
  padding-left: 0;
  padding-right: 0;
}

.statement-level-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 92px;
  padding: 0.45rem 0.8rem;
  border-radius: 999px;
  font-weight: 800;
}

.statement-level-pill-previous {
  background: #fff4df;
  color: #b27400;
}

.statement-level-pill-next {
  background: #fff4df;
  color: #b27400;
}

.statement-level-arrow {
  color: #b27400;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  min-width: 24px;
  height: 24px;
  line-height: 1;
}

.statement-level-arrow.is-up {
  color: #17834a;
}

.statement-level-arrow.is-same {
  color: #b27400;
}

.statement-level-arrow.is-down {
  color: #c73b47;
}

.statement-level-arrow.is-up .icon,
.statement-level-arrow.is-down .icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transform-origin: center center;
}

.statement-level-arrow.is-up .icon {
  transform: rotate(-45deg);
}

.statement-level-arrow.is-down .icon {
  transform: rotate(45deg);
}

.statement-achievement-banner {
  background: linear-gradient(135deg, #e7fff4 0%, #f8fffb 100%);
  border: none;
  box-shadow: none;
  color: #0c8c68;
  font-weight: 800;
}

.statement-detail-card.statement-detail-card-level-flow,
.statement-detail-card.statement-detail-card-achievement {
  display: flex;
  align-items: stretch;
  justify-content: center;
  padding: 0;
  overflow: hidden;
}

.statement-achievement-badge {
  width: 36px;
  height: 36px;
  object-fit: contain;
}

.statement-event-details {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.7rem;
  margin-top: 0.85rem;
}

.statement-event-details.is-compact-balance-event {
  grid-template-columns: repeat(5, minmax(0, 1fr));
}

.statement-detail-card {
  padding: 0.75rem 0.85rem;
  border-radius: 6px;
  background: #fafafa;
  border: 1px solid #efefef;
}

.statement-detail-card.statement-detail-card-achievement {
  background: linear-gradient(135deg, #e7fff4 0%, #f8fffb 100%);
  border: 1px solid #d1f3e4;
}

.statement-detail-card.statement-detail-card-level-flow {
  grid-column: span 1;
  background: transparent;
  border: none;
}

.statement-detail-label {
  display: block;
  color: #767676;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.statement-detail-value {
  display: block;
  margin-top: 0.35rem;
  color: #252525;
  font-size: 0.94rem;
  line-height: 1.3;
}

.statement-empty-state {
  text-align: center;
}

.statement-empty-title {
  font-size: 1.2rem;
  font-weight: 800;
  color: #303030;
}

@media (max-width: 768px) {
  .statement-event-details.is-compact-balance-event {
    grid-template-columns: 1fr;
  }

  .statement-detail-card.statement-detail-card-level-flow {
    grid-column: span 1;
  }

  .statement-event-header {
    flex-direction: column;
  }

  .statement-event {
    grid-template-columns: 1fr;
  }

  .statement-event-rail {
    justify-content: flex-start;
    min-height: 52px;
  }

  .statement-event-line {
    left: 25px;
  }

  .statement-event-highlight {
    align-items: flex-start;
  }
}
</style>
